import { NETWORK_DASHBOARD_STORE_AGE_FILTERS } from '@gozoki/api-types'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect } from 'react'
import z from 'zod/v3'
import { PlanningDashboard } from '~/components/routes/hr/planning/dashboard'
import {
  HR_DATE_PRESETS,
  hrDashboardPeriodLabel,
} from '~/components/routes/hr/planning/dashboard/hr-dashboard-range'
import { useHrPeriodWindow } from '~/components/routes/hr/planning/dashboard/use-hr-period-window'
import { HrDashboardSelector } from '~/components/routes/hr/planning/dashboard/hr-dashboard-selector'
import { HrSlotPortal } from '~/components/routes/hr/planning/hr-toolbar-slot'
import { HR_DASHBOARD_READ } from '~/components/routes/hr/tabs'
import { SelectionPeriodBadge } from '~/components/dashboards/selection-period-badge'
import { useFeatureFlag, useFeatureFlagsReady } from '~/hooks/useFeatureFlag'
import { withRoutePermissions } from '~/utils/rbac'

// ISO 'YYYY-MM-DD' pour les params de plage du dashboard.
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

export const Route = createFileRoute('/_authenticated/hr/planning/dashboard')({
  staticData: {
    permission: HR_DASHBOARD_READ,
  },
  component: RouteComponent,
  validateSearch: z.object({
    // Multi-sélection du Tableau de bord — état séparé de storeId (aucune synchro).
    storeIds: z.array(z.number()).optional(),
    // Filtre ancienneté boutique (réutilise l'accordéon DashboardFilters). `default` = pas de filtre ;
    // `ageStoreIds` porte la sélection résolue quand elle ne matche aucun préset. Persistés pour
    // restaurer l'état de l'accordéon à la réouverture de la modale.
    storeAgeFilter: z.enum(NETWORK_DASHBOARD_STORE_AGE_FILTERS).optional().default('default'),
    ageStoreIds: z.array(z.number()).optional(),
    // Plage de dates libre + préset (réutilise le sélecteur du Network Dashboard).
    startDate: isoDate.optional(),
    endDate: isoDate.optional(),
    datePreset: z.enum(HR_DATE_PRESETS).optional(),
  }),
})
withRoutePermissions(Route)

function RouteComponent() {
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const dashboardRhDisplayed = useFeatureFlag('dashboard-rh')
  const flagsReady = useFeatureFlagsReady()

  // Onglet masqué tant que le flag est off : un accès direct à l'URL est renvoyé sur Planning.
  useEffect(() => {
    if (flagsReady && !dashboardRhDisplayed) {
      navigate({ to: '/hr/planning/calendar', search: (prev) => ({ ...prev }) })
    }
  }, [flagsReady, dashboardRhDisplayed, navigate])

  // Même fenêtre résolue que `PlanningDashboard` : le badge doit annoncer la période réellement
  // interrogée, pas la sentinelle « Toujours » (qui afficherait « 2 431 jours glissants »).
  const { periodStartIso, endIso } = useHrPeriodWindow(search)

  return (
    <>
      <HrSlotPortal name="header">
        <SelectionPeriodBadge
          startDate={periodStartIso}
          endDate={endIso}
          label={hrDashboardPeriodLabel(periodStartIso, endIso)}
        />
      </HrSlotPortal>
      <HrSlotPortal name="toolbar">
        <HrDashboardSelector />
      </HrSlotPortal>
      <PlanningDashboard />
    </>
  )
}
